import { DateFormat, formatDate } from "common";
import { Dayjs } from "dayjs";
import { difference, intersection, isEmpty, reduce, sumBy } from "lodash";
import { ElasticsearchResponse, PaymentMethod, Seller, Source, SourceCopy, TransactionType } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { purchaseTypes } from "../models/Transaction";
import { AnalyticsMode } from "../stores/reports/analyticsFilterStore";
import { EarningsReportFilter, EarningsReportResponse } from "../stores/reports/earningsReportFilterStore";

export interface EarningsReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    filters: EarningsReportFilter;
}

export const getEarningsReport = async (params: EarningsReportParams): Promise<EarningsReportResponse> => {
    const { seller } = params;
    const index = config.elasticsearch.indexes.transaction;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));

    return getReport(data);
};

interface BucketProps {
    doc_count: number;
    items: {
        doc_count: number;
        stats_commission: {
            sum: number;
        };
        stats_earning: {
            sum: number;
        };
    };
    key: string;
}

const getReport = (data: any): EarningsReportResponse => {
    const total = sumBy(data.aggregations.total.methods.buckets, "items.stats_earning.sum");
    const methods = data.aggregations.total.methods.buckets.map((bucket: BucketProps) => {
        if (bucket.key === PaymentMethod.CREDIT_CARD) {
            return {
                method: bucket.key,
                total: bucket.items.stats_earning.sum,
                paid: bucket.items.stats_earning.sum + bucket.items.stats_commission.sum,
                commission: -1 * bucket.items.stats_commission.sum,
            };
        }

        return {
            method: bucket.key,
            total: bucket.items.stats_earning.sum,
        };
    });

    return { total, methods };
};

const getPayload = ({ seller, from, to, filters }: EarningsReportParams) => {
    const query: any = getBasePayload(seller, from, to, filters);

    const itemsNetAggregation = query.aggs;
    itemsNetAggregation.total.aggs.items.aggs.stats_commission = { stats: { field: "items.commission" } };

    const methodAggs = { terms: { field: "method", size: 9_999_999 }, aggs: itemsNetAggregation.total.aggs };
    query.aggs.total.aggs = { methods: methodAggs };

    return query;
};

export const getRefundReserveReport = async (params: EarningsReportParams) => {
    const { seller } = params;
    const index = config.elasticsearch.indexes.transaction;
    const { data } = await api.post<ElasticsearchResponse>(
        `/elasticsearch/${index}/_search?seller=${seller.id}`,
        getRefundReservePayload(params),
    );

    return data;
};

const getRefundReservePayload = ({ seller, from, to, filters }: EarningsReportParams) => {
    const query: any = getBasePayload(seller, from, to, filters);
    query.query.bool.must.push({ exists: { field: "escrowedAt" } });

    return query;
};

// TODO: To be extracted once Xolabot earnings report is done, because it uses this too
const getBasePayload = (seller: Seller, from: Dayjs, to: Dayjs, filters: EarningsReportFilter) => {
    const isCashFlow = filters.mode === AnalyticsMode.CASH_FLOW;
    const netAggregation = {
        items: {
            nested: { path: "items" },
            aggs: { stats_earning: { stats: { field: isCashFlow ? "items.net" : "items.realizableAmount" } } },
        },
    };

    const start = formatDate(from, DateFormat.DATE_ISO);
    const end = formatDate(to, DateFormat.DATE_ISO);
    const dateRange = { gte: start, lte: end, time_zone: seller.timezoneName };
    let rangeFilter: any = { range: { createdAt: dateRange } };
    if (!isCashFlow) {
        rangeFilter = {
            nested: {
                path: "items",
                query: { bool: { must: [{ range: { "items.realizedAt": dateRange } }] } },
            },
        };
    }

    const must: any = [
        rangeFilter,
        { terms: { type: [...purchaseTypes, TransactionType.DISPUTE, TransactionType.STRIPE_PAYDOWN] } },
        { term: { "seller.id": seller.id } },
    ];

    if (filters.reservationist.selected.length > 0) {
        const users: ID[] = [];
        const sources: string[] = [];
        for (const createdByUser of filters.reservationist.selected) {
            if (createdByUser.length === 24) {
                users.push(createdByUser);
            } else {
                sources.push(createdByUser);
            }
        }

        // Create an 'OR' query that will find all transactions createdBy the user, or "createdBy" that source
        const should = [];
        if (!isEmpty(users)) {
            should.push({ terms: { "createdBy.id": users } });
        }

        if (!isEmpty(sources)) {
            should.push({ terms: { source: sources } });
        }

        // We must exclude checkout transactions unless source "checkout" is explicitly selected.
        // Otherwise, total value will include transactions for the selected users who just accepted the booking. See CS-3224.
        const bool: any = { should };
        const includedSources = intersection(sources, [Source.CHECKOUT, Source.KIOSK]);
        if (!isEmpty(users) && includedSources.length < 2) {
            bool.must_not = { terms: { source: difference([Source.CHECKOUT, Source.KIOSK], includedSources) } };
        }

        must.push({ bool });
    }

    if (filters.paymentMethods.selected.length > 0) {
        must.push({ terms: { method: filters.paymentMethods.selected } });
    }

    return {
        size: 0, // Don't show hits
        query: {
            bool: { must },
        },
        aggs: {
            total: {
                filter: { match_all: { boost: 1 } },
                aggs: netAggregation,
            },
        },
    };
};

export const getEarningReportGroupedTransactions = async (params: EarningsReportParams) => {
    const { seller } = params;
    const index = config.elasticsearch.indexes.transaction;
    const query: any = getGroupedTransactionsPayload(params);
    const { data } = await api.post<any>(`/elasticsearch/${index}/_search?seller=${seller.id}`, query);

    return getGroupedTransactions(data);
};

const NonReservationistSources = [Source.CHECKOUT, Source.GIFT, Source.KIOSK];

const getGroupedTransactionsPayload = ({ seller, from, to, filters }: EarningsReportParams) => {
    const query: any = getBasePayload(seller, from, to, filters);

    const methodAggs = {
        methods: {
            terms: { field: "method", size: 9_999_999 },
            aggs: {
                items: {
                    nested: {
                        path: "items",
                    },
                    aggs: {
                        stats_earning: {
                            stats: {
                                field: "items.net",
                            },
                        },
                        stats_commission: {
                            stats: {
                                field: "items.commission",
                            },
                        },
                    },
                },
                currency: {
                    terms: { field: "currency", size: 9_999_999 },
                },
            },
        },
    };

    return {
        ...query,
        aggs: {
            perreservationist: {
                filter: {
                    bool: {
                        must_not: [{ terms: { source: NonReservationistSources } }],
                    },
                },
                aggs: {
                    reservationists: {
                        terms: {
                            field: "createdBy.id",
                            size: 9_999_999, // Return all terms instead of just the top ones
                        },

                        aggs: methodAggs,
                    },
                },
            },

            checkout: {
                filter: {
                    bool: {
                        must: { term: { source: Source.CHECKOUT } },
                    },
                },
                aggs: methodAggs,
            },

            gift: {
                filter: {
                    bool: {
                        must: { term: { source: Source.GIFT } },
                    },
                },
                aggs: methodAggs,
            },
        },
    };
};

export interface GroupedTransaction {
    id?: ID;
    reservationist: {
        id: ID | null;
        name: string | null;
    };
    paymentMethods: { [key: string]: number };
    total: number;
    currency: string;
}

const getGroupedTransactions = (data?: any) => {
    const { aggregations } = data;
    if (aggregations) {
        const groupedData = reduce<any, GroupedTransaction[]>(
            aggregations,
            (groups, aggregation, key) => {
                if (NonReservationistSources.includes(key as Source)) {
                    const { buckets } = aggregation.methods;
                    if (buckets.length > 0) {
                        const groupTransactions = createGroupTransactions(key, buckets);
                        groups = [...groups, groupTransactions];
                    }
                } else {
                    const { buckets: reservationistBuckets } = aggregation.reservationists;
                    if (reservationistBuckets.length > 0) {
                        for (const bucket of reservationistBuckets) {
                            const { buckets } = bucket.methods;
                            if (buckets.length > 0) {
                                const groupTransactions = createGroupTransactions(bucket.key, buckets);
                                groups = [...groups, groupTransactions];
                            }
                        }
                    }
                }

                return groups;
            },
            [],
        );

        return groupedData;
    }
};

const createGroupTransactions = (key: string, buckets: any[]) => {
    const isNonReservationist = NonReservationistSources.includes(key as Source);
    return {
        id: key,
        reservationist: {
            id: isNonReservationist ? null : key,
            name: isNonReservationist ? SourceCopy[key as Source] : null,
        },
        paymentMethods: getPaymentMethods(buckets),
        currency: buckets[0].currency.buckets[0].key,
        total: sumBy(buckets, "items.stats_earning.sum"),
    };
};

const getPaymentMethods = (buckets: any[]) =>
    reduce(
        buckets,
        (result, bucket) => {
            result[bucket.key] = bucket.items.stats_earning.sum;
            return result;
        },
        {} as any,
    );
